import axios from 'axios'
import type { AuthState, BillingSummary, Invoice, MeshData, PageResponse, StreamingStats, UsageQuota, UserProfile, UserStats } from '../types'

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
})

api.interceptors.request.use((config) => {
  const stored = localStorage.getItem('auth')
  if (stored) {
    const auth: AuthState = JSON.parse(stored)
    if (auth.token) {
      config.headers.Authorization = `Bearer ${auth.token}`
    }
  }
  return config
})

api.interceptors.response.use(
  (res) => res,
  (error) => {
    const isAuthEndpoint = error.config?.url?.startsWith('/auth/')
    if (error.response?.status === 401 && !isAuthEndpoint) {
      localStorage.removeItem('auth')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

export const authApi = {
  checkUserId: (userId: string) =>
    api.get<{ available: boolean }>('/auth/check-userid', { params: { userId } }).then((r) => r.data.available),

  register: (data: { userId: string; email: string; password: string; name: string; organization?: string }) =>
    api.post('/auth/register', data).then((r) => r.data),

  login: (data: { userId: string; password: string }) =>
    api.post('/auth/login', data).then((r) => r.data),

  getNaverLoginUrl: () =>
    api.get('/auth/naver').then((r) => r.data.url as string),
}

export const dataApi = {
  upload: (formData: FormData, onProgress?: (pct: number) => void) =>
    api.post<MeshData>('/data', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => {
        if (onProgress && e.total) {
          onProgress(Math.round((e.loaded / e.total) * 100))
        }
      },
    }).then((r) => r.data),

  list: (params?: { name?: string; page?: number; size?: number }) =>
    api.get<PageResponse<MeshData>>('/data', { params }).then((r) => r.data),
  listAll: (params?: { name?: string; page?: number; size?: number }) =>
    api.get<PageResponse<MeshData>>('/data/listAll', { params }).then((r) => r.data),
  get: (id: number) =>
    api.get<MeshData>(`/data/${id}`).then((r) => r.data),

  deleteOriginal: (id: number) =>
    api.delete<MeshData>(`/data/${id}/original`).then((r) => r.data),

  delete: (id: number) =>
    api.delete(`/data/${id}`),

  getSuccessful: () =>
    api.get<MeshData[]>('/data/successful').then((r) => r.data),

  getTileToken: (id: number) =>
    api.get<{ token: string; expiresIn: number }>(`/data/${id}/tiles/token`).then(r => r.data),

  download: async (id: number, dataName: string) => {
    // 단기 토큰(15분)을 받아 브라우저가 직접 스트리밍 — blob 메모리 적재 없음
    const { url } = await api.get<{ url: string }>(`/data/${id}/download/token`).then(r => r.data)
    const a = document.createElement('a')
    a.href = url
    a.download = `${dataName}_tiles.zip`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  },
}

export const userApi = {
  getProfile: () =>
    api.get<UserProfile>('/user/me').then((r) => r.data),

  getStats: () =>
    api.get<UserStats>('/user/stats').then((r) => r.data),

  getStreamingStats: (params: { period: 'daily' | 'weekly' | 'monthly'; dataId?: number; from?: string; to?: string }) =>
    api.get<StreamingStats>('/user/streaming-stats', { params }).then((r) => r.data),

  updateEmail: (email: string) =>
    api.patch<UserProfile>('/user/email', { email }).then((r) => r.data),

  updateLevel: (userLevel: string) =>
    api.patch<UserProfile>('/user/level', { userLevel }).then((r) => r.data),

  cancelScheduledLevel: () =>
    api.delete<UserProfile>('/user/scheduled-level').then((r) => r.data),
}

export const billingApi = {
  getSummary: () =>
    api.get<BillingSummary>('/billing/summary').then((r) => r.data),

  getInvoices: () =>
    api.get<Invoice[]>('/billing/invoices').then((r) => r.data),

  getQuota: () =>
    api.get<UsageQuota>('/billing/quota').then((r) => r.data),
}
